import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/config/queries';
import { supabase } from '@/integrations/supabase/client';
import { getIsoDateInTimezone, getTodayIsoDateInTimezone, getUserTimezone } from '@/lib/dateUtils';
import { createLogger } from '@/lib/structuredLogger';
import {
  clampRecoveryScore,
  type RecoveryInputDraft,
  type RecoveryInputRecord,
} from '@/types/recovery-input';

const logger = createLogger('RecoveryInputs');

export async function invalidateRecoveryInputQueries(queryClient: QueryClient, userId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DAILY_RECOMMENDATION(userId) }),
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.LATEST_RECOVERY_INPUT(userId),
    }),
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === QUERY_KEYS.TODAY_RECOVERY_INPUT('', '')[0] &&
        query.queryKey[1] === userId,
    }),
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.LATEST_CHECKIN(userId),
    }),
  ]);
}

export function useLatestRecoveryInput(userId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.LATEST_RECOVERY_INPUT(userId ?? ''),
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('recovery_inputs')
        .select(
          'id, user_id, source, raw_input_text, transcript_text, sleep_quality_subjective, energy_level, stress_management_score, notes, created_at',
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching latest recovery input', error);
        return null;
      }

      return data as RecoveryInputRecord | null;
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useTodayRecoveryInput(userId?: string, timezone?: string) {
  const resolvedTimezone = timezone?.trim() || getUserTimezone();

  return useQuery({
    queryKey: QUERY_KEYS.TODAY_RECOVERY_INPUT(userId ?? '', resolvedTimezone),
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('recovery_inputs')
        .select(
          'id, user_id, source, raw_input_text, transcript_text, sleep_quality_subjective, energy_level, stress_management_score, notes, created_at',
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching today recovery input', error);
        return null;
      }

      if (!data?.created_at) {
        return null;
      }

      return getIsoDateInTimezone(data.created_at, resolvedTimezone) ===
        getTodayIsoDateInTimezone(resolvedTimezone)
        ? (data as RecoveryInputRecord)
        : null;
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useRecoveryInputManager(userId?: string) {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (draft: RecoveryInputDraft) => {
      if (!userId) {
        throw new Error('You need to be signed in before saving recovery input.');
      }

      const { data, error } = await supabase
        .from('recovery_inputs')
        .insert({
          user_id: userId,
          source: draft.source,
          raw_input_text: draft.raw_input_text?.trim() || null,
          transcript_text: draft.transcript_text?.trim() || null,
          sleep_quality_subjective: clampRecoveryScore(draft.sleep_quality_subjective),
          energy_level: clampRecoveryScore(draft.energy_level),
          stress_management_score: clampRecoveryScore(draft.stress_management_score),
          notes: draft.notes?.trim() || null,
        })
        .select(
          'id, user_id, source, raw_input_text, transcript_text, sleep_quality_subjective, energy_level, stress_management_score, notes, created_at',
        )
        .single();

      if (error) {
        throw error;
      }

      return data as RecoveryInputRecord;
    },
    onSuccess: async () => {
      if (!userId) return;
      await invalidateRecoveryInputQueries(queryClient, userId);
    },
  });

  return {
    createMutation,
  };
}
